// Loads/saves PersonalRx flows against Supabase.
// The public widget uses `loadPublishedFlow`; the admin editor uses
// `loadDraftFlow`/`saveDraftFlow`.

import { supabase } from '@/integrations/supabase/client';
import type {
  RxFlow,
  RxFlowNode,
  RxFlowOption,
  RxFlowVersion,
} from '@/types/personalRxFlow';

const CATEGORY_ORDER = [
  'hair',
  'face',
  'nails',
  'general',
];

type FlowVersionRow = {
  id: string;
  version: number;
  status: 'draft' | 'published' | 'archived';
  published_at: string | null;
  notes: string | null;
};

type NodeRow = {
  id: string;
  version_id: string;
  key: string;
  type: RxFlowNode['type'];
  label: string | null;
  hint: string | null;
  placeholder: string | null;
  question_type: RxFlowNode['questionType'];
  required: boolean;
  category: string | null;
  position: number;
  editor_x: number | null;
  editor_y: number | null;
  config: Record<string, unknown> | null;
};

type OptionRow = {
  id: string;
  version_id: string;
  node_id: string;
  option_key: string;
  label: string;
  value: string;
  position: number;
  next_type: RxFlowOption['nextType'];
  next_node_id: string | null;
};

function assembleFlow(
  version: FlowVersionRow,
  nodes: NodeRow[],
  options: OptionRow[],
): RxFlow {
  const optsByNode = new Map<string, RxFlowOption[]>();
  for (const o of options) {
    const list = optsByNode.get(o.node_id) ?? [];
    list.push({
      id: o.id,
      nodeId: o.node_id,
      optionKey: o.option_key,
      label: o.label,
      value: o.value,
      position: o.position,
      nextType: o.next_type,
      nextNodeId: o.next_node_id,
    });
    optsByNode.set(o.node_id, list);
  }
  for (const list of optsByNode.values()) {
    list.sort((a, b) => a.position - b.position);
  }
  const flowNodes: RxFlowNode[] = nodes.map((n) => ({
    id: n.id,
    key: n.key,
    type: n.type,
    label: n.label,
    hint: n.hint,
    placeholder: n.placeholder,
    questionType: n.question_type,
    required: n.required,
    category: n.category,
    position: n.position,
    editorX: n.editor_x,
    editorY: n.editor_y,
    config: n.config ?? {},
    options: optsByNode.get(n.id) ?? [],
  }));
  const start = flowNodes.find((n) => n.type === 'start');
  const contact = flowNodes.find((n) => n.type === 'contact_form');
  const success = flowNodes.find((n) => n.type === 'success');
  const versionInfo: RxFlowVersion = {
    id: version.id,
    version: version.version,
    status: version.status,
    publishedAt: version.published_at,
    notes: version.notes,
  };
  return {
    version: versionInfo,
    startNodeId: start?.id ?? flowNodes[0]?.id ?? '',
    contactFormNodeId: contact?.id ?? null,
    successNodeId: success?.id ?? null,
    categoryOrder: CATEGORY_ORDER,
    nodes: flowNodes,
  };
}

async function loadFlowByVersionId(versionId: string): Promise<RxFlow | null> {
  const [{ data: ver }, { data: nodes }, { data: options }] = await Promise.all([
    supabase.from('personal_rx_flow_versions').select('*').eq('id', versionId).maybeSingle(),
    supabase.from('personal_rx_flow_nodes').select('*').eq('version_id', versionId),
    supabase.from('personal_rx_flow_options').select('*').eq('version_id', versionId),
  ]);
  if (!ver) return null;
  return assembleFlow(ver as FlowVersionRow, (nodes ?? []) as NodeRow[], (options ?? []) as OptionRow[]);
}

export async function loadPublishedFlow(): Promise<RxFlow | null> {
  const { data: ver } = await supabase
    .from('personal_rx_flow_versions')
    .select('*')
    .eq('status', 'published')
    .maybeSingle();
  if (!ver) return null;
  return loadFlowByVersionId(ver.id);
}

export async function loadDraftFlow(): Promise<RxFlow | null> {
  const { data: ver } = await supabase
    .from('personal_rx_flow_versions')
    .select('*')
    .eq('status', 'draft')
    .order('version', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!ver) return null;
  return loadFlowByVersionId(ver.id);
}

export async function loadFlowVersions(): Promise<RxFlowVersion[]> {
  const { data } = await supabase
    .from('personal_rx_flow_versions')
    .select('*')
    .order('version', { ascending: false });
  return ((data ?? []) as FlowVersionRow[]).map((v) => ({
    id: v.id,
    version: v.version,
    status: v.status,
    publishedAt: v.published_at,
    notes: v.notes,
  }));
}
